import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr, fmtMin, fmtDateTime } from "@/lib/api";
import { PageHeader, StatusBadge, FlagBadge } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  FileDown, FileSpreadsheet, FileText, Mail, Plus, Link2, RotateCw, Ban,
  Copy, Eye, Loader2, CalendarPlus, UploadCloud, Trash2, Pencil, PencilLine,
} from "lucide-react";
import { toast } from "sonner";
import ImportWizard from "./ImportWizard";
import AssessmentsTab from "./AssessmentsTab";
import EditHoursDialog from "./EditHoursDialog";
import TeacherLetterDialog from "./TeacherLetterDialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CourseDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [attendance, setAttendance] = useState(null);

  const loadAll = useCallback(() => {
    api.get(`/courses/${id}`).then((r) => setCourse(r.data));
    api.get(`/courses/${id}/lessons`).then((r) => setLessons(r.data));
    api.get(`/courses/${id}/enrollments`).then((r) => setEnrollments(r.data));
    api.get(`/courses/${id}/attendance`).then((r) => setAttendance(r.data));
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (!course) return <div className="text-slate-500">Caricamento…</div>;

  const downloadExport = (fmt) => {
    const token = localStorage.getItem("athena_token");
    fetch(`${BACKEND}/api/courses/${id}/export?format=${fmt}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = `registro_${course.code}.${fmt === "xlsx" ? "xlsx" : fmt}`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div>
      <button onClick={() => navigate("/corsi")} className="text-sm text-slate-500 hover:text-slate-800 mb-2">← Tutti i corsi</button>
      <PageHeader title={course.title} subtitle={[course.code, course.edition, `Soglia max assenza ${course.max_absence_pct}%`].filter(Boolean).join(" · ")}>
        <span className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full capitalize">{course.status}</span>
      </PageHeader>

      <Tabs defaultValue={searchParams.get("tab") || "registro"}>
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="registro" data-testid="tab-registro">Registro</TabsTrigger>
          <TabsTrigger value="lezioni" data-testid="tab-lezioni">Lezioni</TabsTrigger>
          <TabsTrigger value="partecipanti" data-testid="tab-partecipanti">Partecipanti</TabsTrigger>
          <TabsTrigger value="importa" data-testid="tab-importa">Importa CSV</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">E-mail</TabsTrigger>
          <TabsTrigger value="verifiche" data-testid="tab-verifiche">Verifiche</TabsTrigger>
          <TabsTrigger value="documentazione" data-testid="tab-documentazione">Documentazione</TabsTrigger>
        </TabsList>

        <TabsContent value="registro">
          <RegisterTab course={course} attendance={attendance} navigate={navigate} downloadExport={downloadExport} onChanged={loadAll} />
        </TabsContent>
        <TabsContent value="lezioni">
          <LessonsTab course={course} lessons={lessons} reload={loadAll} />
        </TabsContent>
        <TabsContent value="partecipanti">
          <ParticipantsTab course={course} enrollments={enrollments} reload={loadAll} />
        </TabsContent>
        <TabsContent value="importa">
          <ImportWizard courseId={id} lessons={lessons} enrollments={enrollments} onDone={loadAll} initialLessonId={searchParams.get("lesson") || ""} />
        </TabsContent>
        <TabsContent value="email">
          <EmailTab course={course} lessons={lessons} enrollments={enrollments} />
        </TabsContent>
        <TabsContent value="verifiche">
          <AssessmentsTab course={course} />
        </TabsContent>
        <TabsContent value="documentazione">
          <DocsTab course={course} enrollments={enrollments} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Documentazione ---------------- */
const downloadFile = async (path, filename, setBusy) => {
  setBusy?.(path);
  try {
    const r = await fetch(`${BACKEND}/api${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem("athena_token")}` } });
    if (!r.ok) throw new Error();
    const url = URL.createObjectURL(await r.blob()); const a = document.createElement("a");
    a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
  } catch { toast.error("Download non riuscito"); }
  setBusy?.(null);
};

function DocAction({ title, desc, children, testid }) {
  return (
    <Card className="p-5 border-slate-200 flex flex-col" data-testid={testid}>
      <div className="font-semibold text-slate-900">{title}</div>
      <p className="text-xs text-slate-500 mt-1 mb-4 flex-1">{desc}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </Card>
  );
}

function DocsTab({ course, enrollments }) {
  const [busy, setBusy] = useState(null);
  const code = course.code;
  const final = ["concluso", "archiviato"].includes(course.status);
  const btn = (path, filename, label, testid, primary) => (
    <Button key={path} size="sm" variant={primary ? "default" : "outline"} disabled={!!busy} data-testid={testid}
      onClick={() => downloadFile(path, filename, setBusy)} className={primary ? "bg-[#008C45] hover:bg-[#006B34]" : ""}>
      {busy === path ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />} {label}
    </Button>
  );
  return (
    <div className="space-y-5">
      {final ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 font-medium" data-testid="docs-final-banner">
          Corso concluso – documentazione definitiva
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="docs-provisional-banner">
          Corso {course.status}: la documentazione è provvisoria e aggiornata alla data odierna.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DocAction testid="docs-archive" title="Documentazione completa (ZIP)" desc="Un unico archivio ordinato in cartelle: registro (PDF, Excel, CSV), schede individuali, calendario lezioni, CSV presenze importati e riepilogo e-mail.">
          {btn(`/courses/${course.id}/docs/archive.zip`, `documentazione_${code}.zip`, "Scarica tutto", "docs-archive-btn", true)}
        </DocAction>
        <DocAction testid="docs-register" title="Registro completo del corso" desc="Registro presenze con ore frequentate, assenze e andamento di tutti gli iscritti.">
          {btn(`/courses/${course.id}/export?format=pdf`, `registro_${code}.pdf`, "PDF", "docs-register-pdf")}
          {btn(`/courses/${course.id}/export?format=xlsx`, `registro_${code}.xlsx`, "Excel", "docs-register-xlsx")}
          {btn(`/courses/${course.id}/export?format=csv`, `registro_${code}.csv`, "CSV", "docs-register-csv")}
        </DocAction>
        <DocAction testid="docs-cards" title="Tutte le Schede Individuali" desc={`Le schede presenze in PDF di tutti i ${enrollments.length} iscritti, in un unico file ZIP.`}>
          {btn(`/courses/${course.id}/docs/cards.zip`, `schede_individuali_${code}.zip`, "Scarica ZIP", "docs-cards-btn")}
        </DocAction>
      </div>
      <Card className="border-slate-200 overflow-hidden" data-testid="docs-cards-list">
        <div className="px-4 py-3 bg-slate-50 text-sm font-semibold text-slate-700">Schede Individuali Presenze</div>
        <Table>
          <TableBody>
            {[...enrollments].sort((a, b) => `${a.participant?.last_name}`.localeCompare(`${b.participant?.last_name}`)).map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-medium">{e.participant?.last_name} {e.participant?.first_name}</TableCell>
                <TableCell className="text-xs font-mono text-slate-500">{e.participant?.fiscal_code || "—"}</TableCell>
                <TableCell className="text-right">
                  {btn(`/enrollments/${e.id}/export?format=pdf`, `scheda_${e.participant?.last_name}_${e.participant?.first_name}.pdf`, "Scheda PDF", `docs-card-${e.id}`)}
                </TableCell>
              </TableRow>
            ))}
            {enrollments.length === 0 && <TableRow><TableCell className="text-center text-slate-400 py-6">Nessun iscritto.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* ---------------- Registro ---------------- */
const REG_COLS = [
  { k: "name", l: "Cognome e Nome", v: (r) => `${r.participant.last_name} ${r.participant.first_name}`.toLowerCase() },
  { k: "att", l: "Ore frequentate", v: (r) => r.summary.total_recognized_minutes },
  { k: "pres", l: "Frequenza (%)", v: (r) => r.summary.presence_pct_progressive },
  { k: "abs", l: "Assenza (%)", v: (r) => r.summary.absence_pct_progressive },
  { k: "acc", l: "Ore assenza accumulate", v: (r) => r.summary.total_absence_minutes },
  { k: "res", l: "Ore assenza residue", v: (r) => r.summary.residual_absence_minutes },
  { k: "trend", l: "Andamento", v: (r) => r.summary.absence_used_pct },
];

const rowTone = (s) => (s.over_limit ? "bg-rose-50 hover:bg-rose-100" : ["attenzione", "rischio"].includes(s.trend) ? "bg-amber-50 hover:bg-amber-100" : "");

function RegisterTab({ course, attendance, navigate, downloadExport, onChanged }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [editRow, setEditRow] = useState(null);
  const [trendFilter, setTrendFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ k: "name", dir: 1 });
  if (!attendance) return <div className="text-slate-500">Caricamento…</div>;

  let rows = attendance.rows;
  if (trendFilter === "over") rows = rows.filter((r) => r.summary.over_limit);
  else if (trendFilter !== "all") rows = rows.filter((r) => r.summary.trend === trendFilter);
  if (q) {
    const t = q.toLowerCase();
    rows = rows.filter((r) => [r.participant.last_name, r.participant.first_name, r.participant.email, r.participant.fiscal_code]
      .some((x) => (x || "").toLowerCase().includes(t)));
  }
  const col = REG_COLS.find((c) => c.k === sort.k);
  rows = [...rows].sort((a, b) => (col.v(a) > col.v(b) ? 1 : col.v(a) < col.v(b) ? -1 : 0) * sort.dir);
  const toggleSort = (k) => setSort((s) => ({ k, dir: s.k === k ? -s.dir : 1 }));
  const first = attendance.rows[0]?.summary;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Input placeholder="Cerca cognome, nome, e-mail, codice fiscale…" value={q} onChange={(e) => setQ(e.target.value)} className="w-80" data-testid="register-search" />
          <Select value={trendFilter} onValueChange={setTrendFilter}>
            <SelectTrigger className="w-[200px]" data-testid="filter-trend-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli andamenti</SelectItem>
              <SelectItem value="regolare">Regolare</SelectItem>
              <SelectItem value="attenzione">Attenzione</SelectItem>
              <SelectItem value="rischio">Rischio</SelectItem>
              <SelectItem value="over">Limite superato</SelectItem>
              <SelectItem value="nd">N/D (nessuna lezione svolta)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadExport("csv")} data-testid="export-csv-btn"><FileDown size={15} className="mr-1" /> CSV</Button>
          <Button variant="outline" size="sm" onClick={() => downloadExport("xlsx")} data-testid="export-xlsx-btn"><FileSpreadsheet size={15} className="mr-1" /> Excel</Button>
          <Button variant="outline" size="sm" onClick={() => downloadExport("pdf")} data-testid="export-pdf-btn"><FileText size={15} className="mr-1" /> PDF</Button>
        </div>
      </div>
      {first && (
        <Card className="p-4 mb-4 border-slate-200 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="course-progress-card">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Ore svolte del corso</div>
            <div className="text-2xl font-bold tabular text-slate-900" data-testid="course-hours-done">{first.total_programmed_hm} <span className="text-sm font-normal text-slate-500">su {first.course_total_hm}</span></div>
          </div>
          <div className="border-l border-slate-200 pl-8">
            <div className="text-xs uppercase tracking-wide text-slate-500">Ore max assenza</div>
            <div className="text-2xl font-bold tabular text-rose-700" data-testid="course-max-absence">{first.max_absence_hm} <span className="text-sm font-normal text-slate-500">({course.max_absence_pct}%)</span></div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-[#008C45]" style={{ width: `${first.course_total_minutes ? Math.min(100, first.total_programmed_minutes / first.course_total_minutes * 100) : 0}%` }} />
            </div>
            <div className="text-xs text-slate-500 mt-1">{first.completed_lessons} lezioni svolte · Assenza % calcolata sul monte ore totale · Frequenza % calcolata sulle ore delle lezioni svolte</div>
          </div>
        </Card>
      )}

      <Card className="border-slate-200 overflow-x-auto" data-testid="attendance-data-table">
        <Table>
          <TableHeader className="bg-slate-50"><TableRow>
            {REG_COLS.map((c) => (
              <TableHead key={c.k} onClick={() => toggleSort(c.k)} data-testid={`sort-${c.k}`} className="cursor-pointer select-none whitespace-nowrap hover:text-slate-900">
                {c.l} {sort.k === c.k ? (sort.dir === 1 ? "▲" : "▼") : ""}
              </TableHead>
            ))}
            {isSuper && <TableHead />}
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => {
              const s = r.summary;
              return (
                <TableRow key={r.enrollment_id} data-testid={`register-row-${r.enrollment_id}`}
                  className={`cursor-pointer ${rowTone(s)}`} onClick={() => navigate(`/iscrizioni/${r.enrollment_id}`)}>
                  <TableCell className="font-medium text-slate-900 whitespace-nowrap">{r.participant.last_name} {r.participant.first_name}</TableCell>
                  <TableCell className="tabular text-emerald-700 font-semibold" data-testid={`attended-${r.enrollment_id}`}>{s.total_recognized_hm}</TableCell>
                  <TableCell className="tabular text-emerald-700" data-testid={`presence-pct-${r.enrollment_id}`}>{s.presence_pct_progressive}%</TableCell>
                  <TableCell className="tabular text-rose-700">{s.absence_pct_progressive}%</TableCell>
                  <TableCell className="tabular font-semibold">{s.total_absence_hm}</TableCell>
                  <TableCell className={`tabular ${s.over_limit ? "text-rose-700 font-semibold" : ""}`}>{s.residual_absence_hm}</TableCell>
                  <TableCell data-testid={`trend-${r.enrollment_id}`}><FlagBadge flag={r.flag} /></TableCell>
                  {isSuper && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" onClick={() => setEditRow(r)} data-testid={`edit-hours-${r.enrollment_id}`}>
                        <PencilLine size={14} className="mr-1" /> Ore
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={isSuper ? 8 : 7} className="text-center text-slate-400 py-8">Nessun partecipante.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      <EditHoursDialog row={editRow} onClose={() => setEditRow(null)} onSaved={(close = true) => { onChanged(); if (close) setEditRow(null); }} />
    </div>
  );
}

/* ---------------- Lezioni ---------------- */
const minutesBetween = (s, e) => {
  if (!s || !e) return null;
  const [sh, sm] = s.split(":").map(Number); const [eh, em] = e.split(":").map(Number);
  const d = eh * 60 + em - (sh * 60 + sm);
  return d > 0 ? d : null;
};

const plusWeek = (d) => {
  if (!d) return "";
  const x = new Date(`${d}T12:00:00`); x.setDate(x.getDate() + 7);
  return x.toISOString().slice(0, 10);
};

function LessonDialog({ courseId, lesson, template, nextNumber, open, onClose, onSaved }) {
  const blank = { number: nextNumber, title: `Lezione ${nextNumber}`, date: "", start_time: "", end_time: "",
    duration_minutes: 240, teacher: "", teacher_id: null, tutor: "", status: "programmata", notes: "", counts_hours: true };
  const [form, setForm] = useState(blank);
  const [teachers, setTeachers] = useState([]);
  useEffect(() => { if (open) { api.get("/teachers/options").then((r) => setTeachers(r.data)); } }, [open]);
  useEffect(() => {
    if (!open) { return; }
    if (lesson) setForm({ ...blank, ...lesson });
    else if (template) {
      const { start_time, end_time, duration_minutes, teacher, teacher_id, tutor, notes, date } = template;
      setForm({ ...blank, start_time, end_time, duration_minutes, teacher, teacher_id, tutor, notes, date: plusWeek(date) });
    } else setForm(blank);
  }, [open]); // eslint-disable-line

  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    if (k === "start_time" || k === "end_time") {
      const d = minutesBetween(next.start_time, next.end_time);
      if (d) next.duration_minutes = d;
    }
    setForm(next);
  };
  const save = async () => {
    if (!form.number || !form.duration_minutes) { toast.error("Numero e durata sono obbligatori"); return; }
    const body = { ...form, number: Number(form.number), duration_minutes: Number(form.duration_minutes), date: form.date || null,
      start_time: form.start_time || null, end_time: form.end_time || null };
    try {
      if (lesson) await api.put(`/lessons/${lesson.id}`, body);
      else await api.post(`/courses/${courseId}/lessons`, body);
      toast.success(lesson ? "Lezione aggiornata" : "Lezione aggiunta"); onSaved(); onClose();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="lesson-dialog">
        <DialogHeader><DialogTitle>{lesson ? `Modifica lezione ${lesson.number}` : template ? `Duplica lezione ${template.number}` : "Aggiungi lezione"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><Label>N°</Label><Input type="number" data-testid="lesson-number" value={form.number} onChange={set("number")} /></div>
          <div className="col-span-1 sm:col-span-3"><Label>Titolo</Label><Input data-testid="lesson-title" value={form.title} onChange={set("title")} /></div>
          <div className="col-span-2 sm:col-span-1"><Label>Data</Label><Input type="date" data-testid="lesson-date" value={form.date || ""} onChange={set("date")} /></div>
          <div><Label>Inizio</Label><Input type="time" data-testid="lesson-start" value={form.start_time || ""} onChange={set("start_time")} /></div>
          <div><Label>Fine</Label><Input type="time" data-testid="lesson-end" value={form.end_time || ""} onChange={set("end_time")} /></div>
          <div><Label>Durata (min)</Label><Input type="number" data-testid="lesson-duration" value={form.duration_minutes} onChange={set("duration_minutes")} /></div>
          <div className="col-span-2"><Label>Docente</Label>
            <select data-testid="lesson-teacher" value={form.teacher_id || ""} className="w-full h-9 rounded-md border border-input px-2 text-sm"
              onChange={(e) => { const t = teachers.find((x) => x.id === e.target.value); setForm({ ...form, teacher_id: t?.id || null, teacher: t?.name || "" }); }}>
              <option value="">— Nessun docente —</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {!form.teacher_id && form.teacher && <p className="text-[11px] text-amber-700 mt-1">Non in archivio: «{form.teacher}». Aggiungilo in Docenti per collegarlo.</p>}
          </div>
          <div className="col-span-2"><Label>Tutor</Label><Input data-testid="lesson-tutor" value={form.tutor || ""} onChange={set("tutor")} /></div>
          <div className="col-span-2 sm:col-span-4"><Label>Note / programma (un argomento per riga: compaiono negli «Argomenti da trattare» della lettera di incarico)</Label><Textarea rows={4} data-testid="lesson-notes" value={form.notes || ""} onChange={set("notes")} /></div>
          <label className="col-span-2 sm:col-span-4 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" data-testid="lesson-counts-hours" checked={form.counts_hours !== false}
              onChange={(e) => setForm({ ...form, counts_hours: e.target.checked })} className="h-4 w-4 accent-[#008C45]" />
            Conta nel monte ore e nelle presenze (togli la spunta per esami o incontri fuori monte ore)
          </label>
        </div>
        <DialogFooter>
          <Button data-testid="save-lesson-btn" onClick={save} className="bg-[#008C45] hover:bg-[#006B34]">{lesson ? "Salva modifiche" : "Aggiungi lezione"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LessonsTab({ course, lessons, reload }) {
  const [dialog, setDialog] = useState({ open: false, lesson: null, template: null });
  const [imports, setImports] = useState([]);
  const [toDelete, setToDelete] = useState(null);
  const [calResult, setCalResult] = useState(null);
  const [calLoading, setCalLoading] = useState(false);
  const [calPdf, setCalPdf] = useState(null);
  const [letterOpen, setLetterOpen] = useState(false);
  const { user } = useAuth();
  const uploadCalendar = async (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setCalLoading(true);
    try {
      const { data } = await api.post(`/courses/${course.id}/lessons/import-calendar`, fd);
      setCalResult(data); reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setCalLoading(false);
  };
  useEffect(() => { api.get(`/courses/${course.id}/imports`).then((r) => setImports(r.data)); }, [course.id, lessons]);
  const lessonNum = (lid) => lessons.find((l) => l.id === lid)?.number ?? "?";
  const deleteImport = async () => {
    try {
      const { data } = await api.delete(`/imports/${toDelete.id}`);
      toast.success(`Importazione eliminata: ${data.sessions_removed} accessi rimossi, conteggi ricalcolati`);
      setToDelete(null); reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const nextNumber = lessons.reduce((m, l) => Math.max(m, l.number), 0) + 1;
  const totalMin = lessons.filter((l) => l.status !== "annullata" && l.counts_hours !== false).reduce((s, l) => s + l.duration_minutes, 0);

  const setStatus = async (l, status) => {
    await api.put(`/lessons/${l.id}`, { ...l, status }); reload();
  };
  const del = async (l) => {
    if (!window.confirm(`Eliminare la lezione ${l.number}? Verranno rimosse anche le presenze importate per questa lezione.`)) return;
    await api.delete(`/lessons/${l.id}`); toast.success("Lezione eliminata"); reload();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-slate-600" data-testid="lessons-total">
          {lessons.length} lezioni · Monte ore corso: <span className="font-semibold text-slate-900">{fmtMin(totalMin)}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        {user?.role === "super_admin" && (
          <Button variant="outline" onClick={() => setLetterOpen(true)} data-testid="teacher-letter-btn"><FileText size={16} className="mr-1" /> Incarico Docente</Button>
        )}
        <Button variant="outline" disabled={calPdf} onClick={() => downloadFile(`/courses/${course.id}/calendar/pdf`, `calendario_${course.code}.pdf`, setCalPdf)} data-testid="calendar-pdf-btn">
          {calPdf ? <Loader2 size={16} className="mr-1 animate-spin" /> : <FileDown size={16} className="mr-1" />} Scarica calendario PDF
        </Button>
        <label className="inline-flex">
          <input type="file" accept=".xlsx" className="hidden" onChange={uploadCalendar} data-testid="calendar-file-input" />
          <span className="inline-flex items-center h-9 px-4 rounded-md border border-slate-300 text-sm font-medium cursor-pointer hover:bg-slate-50" data-testid="import-calendar-btn">
            {calLoading ? <Loader2 size={16} className="mr-1 animate-spin" /> : <FileSpreadsheet size={16} className="mr-1" />} Importa calendario Excel
          </span>
        </label>
        <Button onClick={() => setDialog({ open: true, lesson: null, template: null })} data-testid="add-lesson-btn" className="bg-[#008C45] hover:bg-[#006B34]">
          <CalendarPlus size={16} className="mr-1" /> Aggiungi lezione
        </Button>
        </div>
      </div>
      <p className="text-xs text-slate-400 -mt-3">Excel calendario: colonne DATA (gg/mm/aaaa), ORARIO (es. 14:30 – 18:30), TITOLO, RELATORE, TUTOR. Le lezioni già presenti con stessa data e ora vengono saltate. Le righe con "Esame" nel titolo non entrano nel monte ore.</p>
      <TeacherLetterDialog course={course} open={letterOpen} onClose={() => setLetterOpen(false)} />
      <Dialog open={!!calResult} onOpenChange={(o) => { if (!o) setCalResult(null); }}>
        <DialogContent data-testid="calendar-summary-dialog">
          <DialogHeader><DialogTitle>Riepilogo importazione calendario</DialogTitle></DialogHeader>
          {calResult && (
            <div className="space-y-3 text-sm">
              <p>Lezioni inserite: <strong data-testid="calendar-imported">{calResult.imported}</strong></p>
              <p>Già presenti (saltate): <strong data-testid="calendar-skipped">{calResult.skipped}</strong></p>
              {calResult.errors.length > 0 && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 max-h-48 overflow-y-auto">
                  {calResult.errors.map((e) => <div key={e} className="text-xs text-rose-700">{e}</div>)}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <LessonDialog courseId={course.id} lesson={dialog.lesson} template={dialog.template} nextNumber={nextNumber} open={dialog.open}
        onClose={() => setDialog({ open: false, lesson: null, template: null })} onSaved={reload} />

      {lessons.length === 0 && (
        <Card className="p-8 border-dashed border-slate-300 text-center text-slate-500" data-testid="lessons-empty">
          Nessuna lezione inserita. Usa "Aggiungi lezione" per impostare il calendario del corso.
        </Card>
      )}

      {lessons.length > 0 && (
        <Card className="border-slate-200 overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50"><TableRow>
              <TableHead>N°</TableHead><TableHead>Titolo</TableHead><TableHead>Data</TableHead><TableHead>Orario</TableHead>
              <TableHead>Durata</TableHead><TableHead>Docente</TableHead><TableHead>Stato</TableHead><TableHead>Cambia stato</TableHead><TableHead>Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {lessons.map((l) => (
                <TableRow key={l.id} data-testid={`lesson-row-${l.number}`}>
                  <TableCell className="font-mono">{l.number}</TableCell>
                  <TableCell>
                    {l.title}
                    {l.counts_hours === false && <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid={`lesson-excluded-${l.number}`}>fuori monte ore</span>}
                  </TableCell>
                  <TableCell className="text-slate-500">{l.date || "—"}</TableCell>
                  <TableCell className="text-slate-500 tabular">{l.start_time && l.end_time ? `${l.start_time}–${l.end_time}` : "—"}</TableCell>
                  <TableCell>{l.duration_minutes}′</TableCell>
                  <TableCell className="text-slate-500">{l.teacher || "—"}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${l.status === "svolta" ? "bg-emerald-50 text-emerald-700" : l.status === "annullata" ? "bg-slate-200 text-slate-600" : "bg-sky-50 text-sky-700"}`}>{l.status}</span></TableCell>
                  <TableCell>
                    <Select value={l.status} onValueChange={(v) => setStatus(l, v)}>
                      <SelectTrigger className="w-[150px] h-8" data-testid={`lesson-status-${l.number}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="programmata">Programmata</SelectItem>
                        <SelectItem value="svolta">Svolta</SelectItem>
                        <SelectItem value="annullata">Annullata</SelectItem>
                        <SelectItem value="rinviata">Rinviata</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" title="Modifica" onClick={() => setDialog({ open: true, lesson: l, template: null })} data-testid={`edit-lesson-${l.number}`}><Pencil size={15} /></Button>
                      <Button variant="ghost" size="sm" title="Duplica" onClick={() => setDialog({ open: true, lesson: null, template: l })} data-testid={`duplicate-lesson-${l.number}`}><Copy size={15} /></Button>
                      <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => del(l)} data-testid={`delete-lesson-${l.number}`}><Trash2 size={15} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Card className="border-slate-200 overflow-hidden" data-testid="lesson-imports-card">
        <div className="px-4 py-3 bg-slate-50 text-sm font-semibold text-slate-700">Importazioni CSV delle lezioni</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Lezione</TableHead><TableHead>File</TableHead><TableHead>Data import</TableHead><TableHead>Accessi inseriti</TableHead><TableHead>Operatore</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {imports.map((i) => (
              <TableRow key={i.id} data-testid={`import-row-${i.id}`}>
                <TableCell className="font-mono">{lessonNum(i.lesson_id)}</TableCell>
                <TableCell className="text-sm font-medium">{i.filename}</TableCell>
                <TableCell className="text-xs text-slate-500">{fmtDateTime(i.confirmed_at || i.created_at)}</TableCell>
                <TableCell>{i.inserted ?? "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{i.created_by}</TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setToDelete(i)} data-testid={`delete-import-${i.id}`}>
                    <Trash2 size={14} className="mr-1" /> Elimina Importazione
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {imports.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">Nessuna importazione CSV.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-import-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina importazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questa importazione? Tutti i dati presenze generati da questo file verranno rimossi e i conteggi saranno ricalcolati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-import-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={deleteImport} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-import-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------------- Partecipanti ---------------- */
function EditParticipantDialog({ participant, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (participant) {
      setForm({ last_name: participant.last_name || "", first_name: participant.first_name || "",
        fiscal_code: participant.fiscal_code || "", email: participant.email || "" });
    }
  }, [participant]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) { toast.error("Formato e-mail non valido"); return; }
    try {
      await api.put(`/participants/${participant.id}`, form);
      toast.success("Anagrafica aggiornata"); onSaved(); onClose();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!participant} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-participant-dialog">
        <DialogHeader><DialogTitle>Modifica anagrafica</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Cognome *</Label><Input data-testid="edit-last" value={form.last_name || ""} onChange={set("last_name")} /></div>
          <div><Label>Nome *</Label><Input data-testid="edit-first" value={form.first_name || ""} onChange={set("first_name")} /></div>
          <div className="col-span-2"><Label>Codice Fiscale</Label><Input data-testid="edit-cf" className="uppercase" value={form.fiscal_code || ""} onChange={set("fiscal_code")} /></div>
          <div className="col-span-2"><Label>E-mail</Label><Input data-testid="edit-email" value={form.email || ""} onChange={set("email")} /></div>
        </div>
        <p className="text-xs text-slate-500">Le modifiche vengono registrate nell'Audit Log con i valori precedenti e i nuovi valori.</p>
        <DialogFooter><Button data-testid="save-participant-btn" onClick={save} className="bg-[#008C45] hover:bg-[#006B34]">Salva modifiche</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportSummaryDialog({ result, onClose }) {
  return (
    <Dialog open={!!result} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="import-summary-dialog">
        <DialogHeader><DialogTitle>Riepilogo importazione partecipanti</DialogTitle></DialogHeader>
        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {[["Importati", result.imported, "text-emerald-700", "imported"], ["Aggiornati", result.updated, "text-sky-700", "updated"], ["Scartati", result.discarded, "text-rose-700", "discarded"]].map(([l, v, c, k]) => (
                <Card key={l} className="p-3 border-slate-200 text-center">
                  <div className="text-xs text-slate-500 uppercase">{l}</div>
                  <div className={`text-2xl font-bold tabular ${c}`} data-testid={`import-summary-${k}`}>{v}</div>
                </Card>
              ))}
            </div>
            <p className="text-sm text-slate-600">Nuove iscrizioni al corso: <strong>{result.enrolled}</strong></p>
            {result.errors.length > 0 && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 max-h-48 overflow-y-auto" data-testid="import-summary-errors">
                <div className="text-xs font-semibold text-rose-700 mb-1">Errori</div>
                {result.errors.map((e) => <div key={e} className="text-xs text-rose-700">{e}</div>)}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ParticipantsTab({ course, enrollments, reload }) {
  const [editing, setEditing] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", company: "", fiscal_code: "", platform_name: "" });
  const [importing, setImporting] = useState(false);

  const save = async () => {
    if (!form.first_name || !form.last_name) { toast.error("Nome e cognome obbligatori"); return; }
    try {
      await api.post(`/courses/${course.id}/participants`, form);
      toast.success("Partecipante iscritto");
      setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", company: "", fiscal_code: "", platform_name: "" });
      reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/courses/${course.id}/participants/import`, fd);
      setImportResult(data);
      reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };
  const regen = async (eid) => { await api.post(`/enrollments/${eid}/regenerate-token`); toast.success("Link rigenerato"); reload(); };
  const revoke = async (eid) => { await api.post(`/enrollments/${eid}/revoke-token`); toast.success("Link revocato"); reload(); };
  const copyLink = (token) => { navigator.clipboard.writeText(`${window.location.origin}/p/${token}`); toast.success("Link copiato"); };
  const remove = async (eid) => { if (window.confirm("Rimuovere l'iscrizione dal corso?")) { await api.delete(`/enrollments/${eid}`); reload(); } };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-enrollment-btn" className="bg-[#0B192C] hover:bg-[#07111E]"><Plus size={16} className="mr-1" /> Nuovo iscritto</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo iscritto al corso</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nome *</Label><Input data-testid="enr-first" value={form.first_name} onChange={set("first_name")} /></div>
              <div><Label>Cognome *</Label><Input data-testid="enr-last" value={form.last_name} onChange={set("last_name")} /></div>
              <div className="col-span-2"><Label>E-mail</Label><Input data-testid="enr-email" value={form.email} onChange={set("email")} /></div>
              <div className="col-span-2"><Label>Nome in videoconferenza</Label><Input value={form.platform_name} onChange={set("platform_name")} /></div>
              <div><Label>Codice fiscale</Label><Input value={form.fiscal_code} onChange={set("fiscal_code")} /></div>
              <div><Label>Azienda/Ente</Label><Input value={form.company} onChange={set("company")} /></div>
            </div>
            <DialogFooter><Button data-testid="save-enrollment-btn" onClick={save} className="bg-[#008C45] hover:bg-[#006B34]">Iscrivi</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <label className="inline-flex">
          <Button asChild variant="outline" disabled={importing} data-testid="import-participants-btn">
            <span className="cursor-pointer">{importing ? <Loader2 size={16} className="mr-1 animate-spin" /> : <UploadCloud size={16} className="mr-1" />} Importa partecipanti (CSV o Excel)</span>
          </Button>
          <input type="file" accept=".csv,text/csv,.xlsx" className="hidden" data-testid="import-participants-input"
            onChange={(e) => { importCsv(e.target.files[0]); e.target.value = ""; }} />
        </label>
        <span className="text-xs text-slate-400">File CSV o Excel (.xlsx) con colonne obbligatorie: Cognome, Nome, Codice Fiscale, E-mail</span>
      </div>
      <EditParticipantDialog participant={editing} onClose={() => setEditing(null)} onSaved={reload} />
      <ImportSummaryDialog result={importResult} onClose={() => setImportResult(null)} />

      <Card className="border-slate-200 overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50"><TableRow>
            <TableHead>Partecipante</TableHead><TableHead>Codice Fiscale</TableHead><TableHead>E-mail</TableHead><TableHead>Link personale</TableHead><TableHead>Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {enrollments.map((e) => (
              <TableRow key={e.id} data-testid={`enrollment-row-${e.id}`}>
                <TableCell className="font-medium">{e.participant?.last_name} {e.participant?.first_name}</TableCell>
                <TableCell className="text-xs font-mono text-slate-500">{e.participant?.fiscal_code || "—"}</TableCell>
                <TableCell className="text-slate-500">{e.participant?.email || "—"}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${e.token_status === "attivo" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{e.token_status}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" title="Modifica anagrafica" onClick={() => setEditing(e.participant)} data-testid={`edit-participant-${e.id}`}><Pencil size={15} /></Button>
                    <Button variant="ghost" size="icon" title="Copia link" onClick={() => copyLink(e.token)} data-testid={`copy-link-${e.id}`}><Copy size={15} /></Button>
                    <Button variant="ghost" size="icon" title="Apri" onClick={() => window.open(`/p/${e.token}`, "_blank")}><Link2 size={15} /></Button>
                    <Button variant="ghost" size="icon" title="Rigenera" onClick={() => regen(e.id)}><RotateCw size={15} /></Button>
                    <Button variant="ghost" size="icon" title="Revoca" onClick={() => revoke(e.id)}><Ban size={15} className="text-rose-500" /></Button>
                    <Button variant="ghost" size="icon" title="Rimuovi" onClick={() => remove(e.id)} data-testid={`remove-enrollment-${e.id}`}><Trash2 size={15} className="text-rose-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {enrollments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Nessun iscritto. Aggiungi un partecipante o importa un CSV.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* ---------------- Email ---------------- */
function EmailModeBanner() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/email-mode").then((r) => setM(r.data)); }, []);
  if (!m) return null;
  return m.mode === "reale" ? (
    <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-4 py-2 mb-4" data-testid="email-mode-banner">
      Invio <strong>REALE</strong> attivo: le e-mail vengono spedite {m.from ? <>da <strong>{m.from}</strong></> : <>a nome di {m.from_name}</>}; le risposte arrivano a {m.reply_to}.
    </div>
  ) : (
    <div className="rounded-lg bg-sky-50 border border-sky-200 text-sky-800 text-sm px-4 py-2 mb-4" data-testid="email-mode-banner">
      Modalità <strong>SIMULAZIONE</strong>: server di posta non configurato, le e-mail vengono registrate ma non inviate.
    </div>
  );
}

function StaffReport({ course, lessonId, onSent }) {
  const staff = [["tutor", "Tutor"], ["project_manager", "Responsabile del Progetto Formativo"]];
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!lessonId) { toast.error("Seleziona una lezione"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${course.id}/lessons/${lessonId}/staff-report`, { recipients: chosen });
      toast.success(`Report presenze in coda per ${data.queued} ${data.queued === 1 ? "destinatario" : "destinatari"}`); setChosen([]); onSent();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="mt-4 pt-4 border-t border-slate-200" data-testid="staff-report">
      <Label>Report presenze della lezione (presente/assente) a Tutor e Responsabile del Progetto Formativo</Label>
      <div className="flex items-center gap-5 flex-wrap mt-2">
        {staff.map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="h-4 w-4 accent-[#0B192C]" disabled={!course[`${k}_email`]} checked={chosen.includes(k)}
              onChange={() => setChosen((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]))} data-testid={`staff-check-${k}`} />
            <span className="font-medium">{label}</span>
            <span className="text-xs text-slate-500">{course[k] || "—"} {course[`${k}_email`] ? `· ${course[`${k}_email`]}` : "(e-mail non inserita nel corso)"}</span>
          </label>
        ))}
        <Button variant="outline" onClick={send} disabled={busy || !chosen.length} data-testid="staff-report-send"><Mail size={15} className="mr-1" /> Invia report presenze</Button>
      </div>
    </div>
  );
}

function EmailTab({ course, lessons, enrollments = [] }) {
  const svolte = lessons.filter((l) => l.status === "svolta");
  const [lessonId, setLessonId] = useState(svolte[svolte.length - 1]?.id || "");
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState([]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const sorted = [...enrollments].sort((a, b) => `${a.participant?.last_name} ${a.participant?.first_name}`.localeCompare(`${b.participant?.last_name} ${b.participant?.first_name}`));

  const loadLogs = useCallback(() => api.get(`/email-logs?course_id=${course.id}`).then((r) => setLogs(r.data)), [course.id]);
  useEffect(() => { loadLogs(); }, [loadLogs]);
  const pending = logs.filter((l) => ["in coda", "in invio"].includes(l.status)).length;
  useEffect(() => {
    if (!pending) return undefined;
    const t = setTimeout(loadLogs, 3000);
    return () => clearTimeout(t);
  }, [logs, pending, loadLogs]);

  const doPreview = async () => {
    if (!lessonId) { toast.error("Seleziona una lezione"); return; }
    const { data } = await api.post(`/courses/${course.id}/lessons/${lessonId}/emails/preview`, { target: "all", enrollment_id: selected[0] || null });
    setPreview(data);
  };
  const send = async (target) => {
    if (!lessonId) { toast.error("Seleziona una lezione"); return; }
    if (target === "selected" && !selected.length) { toast.error("Seleziona almeno un partecipante"); return; }
    if (!(target === "selected" && selected.length === 1) && !window.confirm("Confermi l'invio delle e-mail ai partecipanti selezionati? Se l'invio reale è attivo, i messaggi verranno spediti davvero.")) return;
    setSending(true);
    try {
      const { data } = await api.post(`/courses/${course.id}/lessons/${lessonId}/emails/send`, { target, enrollment_ids: target === "selected" ? selected : [] });
      if (data.queued) toast.success(`${data.queued} e-mail messe in coda: partono automaticamente, l'esito compare nello storico`);
      if (!data.logs.length) toast.info("Nessun destinatario per questo filtro");
      loadLogs();
    }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-5">
      <Card className="p-5 border-slate-200">
        <EmailModeBanner />
        <div className="flex items-end gap-3 flex-wrap">
          <div className="min-w-[240px]">
            <Label>Lezione di riferimento</Label>
            <Select value={lessonId} onValueChange={setLessonId}>
              <SelectTrigger data-testid="email-lesson-select"><SelectValue placeholder="Seleziona lezione svolta…" /></SelectTrigger>
              <SelectContent>{svolte.map((l) => <SelectItem key={l.id} value={l.id}>Lezione {l.number} — {l.date}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Dialog>
            <DialogTrigger asChild><Button variant="outline" onClick={doPreview} data-testid="email-preview-btn"><Eye size={15} className="mr-1" /> Anteprima</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Anteprima e-mail</DialogTitle></DialogHeader>
              {preview && (<div><div className="text-sm text-slate-500 mb-2">A: {preview.recipient}</div><div className="font-semibold mb-2">{preview.subject}</div><pre className="text-xs bg-slate-50 rounded-lg p-4 whitespace-pre-wrap break-all font-mono">{preview.body}</pre></div>)}
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-4">
          <Button onClick={() => send("all")} disabled={sending} data-testid="send-all-btn" className="bg-[#0B192C] hover:bg-[#07111E]">{sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail size={15} className="mr-1" />} Tutti</Button>
          <Button onClick={() => send("absent")} disabled={sending} variant="outline" data-testid="send-absent-btn">Solo assenti</Button>
          <Button onClick={() => send("partial")} disabled={sending} variant="outline" data-testid="send-partial-btn">Presenze parziali</Button>
          <Button onClick={() => send("threshold")} disabled={sending} variant="outline" data-testid="send-threshold-btn">Prossimi/oltre soglia</Button>
        </div>
        <StaffReport course={course} lessonId={lessonId} onSent={loadLogs} />
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <Label>Partecipanti selezionati (qualunque stato)</Label>
            <div className="flex items-center gap-3 text-xs">
              <button className="text-slate-600 hover:underline" onClick={() => setSelected(sorted.map((e) => e.id))} data-testid="select-all-participants">Seleziona tutti</button>
              <button className="text-slate-600 hover:underline" onClick={() => setSelected([])} data-testid="clear-participants">Deseleziona</button>
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="participants-checklist">
            {sorted.map((e) => (
              <label key={e.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                <input type="checkbox" className="h-4 w-4 accent-[#0B192C]" checked={selected.includes(e.id)} onChange={() => toggle(e.id)} data-testid={`email-check-${e.id}`} />
                <span className="font-medium text-slate-800">{e.participant?.last_name} {e.participant?.first_name}</span>
                <span className={`text-xs ${e.participant?.email ? "text-slate-500" : "text-rose-600"}`}>{e.participant?.email || "(e-mail mancante)"}</span>
              </label>
            ))}
            {sorted.length === 0 && <div className="px-3 py-4 text-sm text-slate-400">Nessun iscritto.</div>}
          </div>
          <div className="flex items-center gap-3 flex-wrap mt-3">
            <Button onClick={() => send("selected")} disabled={sending || !selected.length} variant="outline" data-testid="send-selected-btn"><Mail size={15} className="mr-1" /> Invia ai selezionati ({selected.length})</Button>
            {selected.length > 0 && <span className="text-xs text-slate-500">L'anteprima mostra l'e-mail del primo selezionato.</span>}
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 text-sm font-semibold text-slate-700 flex items-center gap-2">Storico e-mail
          {pending > 0 && <span className="text-xs font-normal text-amber-700 flex items-center gap-1" data-testid="email-pending"><Loader2 size={12} className="animate-spin" /> {pending} in invio…</span>}</div>
        <Table>
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Destinatario</TableHead><TableHead>Oggetto</TableHead><TableHead>Tipo</TableHead><TableHead>Esito</TableHead></TableRow></TableHeader>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="text-xs font-mono text-slate-500">{new Date(l.at).toLocaleString("it-IT")}</TableCell>
                <TableCell className="text-sm">{l.recipient}</TableCell>
                <TableCell className="text-sm text-slate-600">{l.subject}</TableCell>
                <TableCell className="text-xs">{l.type}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${["simulata", "inviata"].includes(l.status) ? "bg-emerald-50 text-emerald-700" : l.status === "in coda" || l.status === "in invio" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>{l.status}</span>{l.error && <div className="text-[11px] text-slate-400 mt-0.5 max-w-[220px]">{l.error}</div>}</TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">Nessuna e-mail.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
